/**
 * VideoExporter — unified video export module for Timbrica video tools.
 *
 * Solves three problems that every video tool used to solve by hand:
 *   1. Detect input file's codec / resolution / fps / channels (browser side).
 *   2. Offer a clean UX (format + quality, with collapsed Advanced) and let
 *      the user override per-axis.
 *   3. Generate valid FFmpeg args from the (possibly partial) selection,
 *      maintaining container ↔ codec compatibility (e.g. WebM can't host AAC,
 *      so picking WebM + "preserve audio" auto-swaps to libopus).
 *
 * Does NOT route between engines — webcodecs-encoder.js auto-patches
 * FFmpegProcessor.process(), so tools just call FFmpegProcessor with our args
 * and get HW acceleration for free where applicable.
 *
 * Two layers:
 *   - Headless logic: new VideoExporter(opts) — no DOM dependency
 *   - UI binding:     exporter.bindUI('container-id') — wires the
 *                     <x-video-exporter> Blade component
 *
 * Tool integration (typical):
 *   var exporter = new VideoExporter({
 *       inputFile:       file,
 *       needsFilters:    true,                // burn-in / overlay / scale → forces FFmpeg path
 *       allowedFormats:  ['same', 'mp4', 'mkv'],
 *       allowedQualities:['high', 'medium', 'low'],
 *       captionsAware:   true,                // resize → captions ordering
 *   });
 *   await exporter.detect();
 *   exporter.bindUI('myExporter');            // optional, can be all-headless
 *   // ... user picks ...
 *   var plan = exporter.plan({                // tool extends with its own filters
 *       extraFilters: 'ass=subs.ass:fontsdir=.',
 *       extraOutputArgs: ['-pix_fmt', 'yuv420p'],
 *   });
 *   var blob = await window.FFmpegProcessor.process(plan.file, plan.args, plan.outputName);
 */
(function(){
'use strict';

/* ═══════════════════════════════════════════
   Constants — codec / quality / container matrices
   ═══════════════════════════════════════════ */

/* VP8's -crf only governs quality while the bitrate cap stays out of its way.
   See the libvpx entry below for the measurement. */
var VP8_RATE_CAP = '12M';

/* CRF / bitrate per codec per preset.
   These are the values the user sees as "High / Medium / Low" — mapped
   to per-codec parameters since "CRF 23" means different things to x264 vs vp8. */
var VIDEO_QUALITY = {
    libx264:    { high: ['-crf', '18', '-preset', 'medium'],
                  medium:['-crf', '23', '-preset', 'medium'],
                  low:   ['-crf', '28', '-preset', 'fast'] },
    libx265:    { high: ['-crf', '21', '-preset', 'medium'],
                  medium:['-crf', '26', '-preset', 'medium'],
                  low:   ['-crf', '31', '-preset', 'fast'] },
    /* VP8, not VP9. `libvpx-vp9` always dies with `RuntimeError: memory access
       out of bounds` in @ffmpeg/core@0.12.10 — any frame size, any argument set
       (measured 2026-07-10 across all 18 call sites in this repo). VP8 from the
       same library works. Same CRF numbers as the old VP9 presets so the
       High/Medium/Low the user picks keeps its meaning.
       The `-b:v` cap must never be 0: VP8 then falls back to ffmpeg's 200k
       default and -crf stops mattering (61 KB / 37.4 dB vs 256 KB / 45.8 dB on
       the same clip). 12M is non-binding through 1080p, so -crf governs. */
    /* -cpu-used is uniform on purpose. Varying it per tier (2/4/5, inherited from
       the VP9 presets) made "medium" produce a BIGGER file than "high" at 3 dB
       worse quality — faster settings spend bits badly, so the tier stopped
       meaning anything. With one speed, -crf alone orders both size and quality.
       3 is the knee: at 1080p, cpu-used 2 costs 2x the wall clock (18.7 s vs
       9.4 s per 3 s of video) for the same size and quality. */
    libvpx:     { high: ['-crf', '24', '-b:v', VP8_RATE_CAP, '-deadline', 'good', '-cpu-used', '3'],
                  medium:['-crf', '33', '-b:v', VP8_RATE_CAP, '-deadline', 'good', '-cpu-used', '3'],
                  low:   ['-crf', '44', '-b:v', VP8_RATE_CAP, '-deadline', 'good', '-cpu-used', '3'] },
    /* MPEG-4 Part 2 — the AVI lane (2026-07-29). It has NO -crf at all: quality
       is `-q:v` 1..31 where LOWER is better, and passing a CRF here silently
       encodes at the codec default. The whole point of AVI is opening on old
       hardware, so this stays deliberately conservative rather than chasing
       x264's efficiency. */
    mpeg4:      { high: ['-q:v', '2'],
                  medium:['-q:v', '5'],
                  low:   ['-q:v', '10'] },
};

var AUDIO_BITRATE = {
    aac:        { high: '192k', medium: '128k', low: '96k' },
    libopus:    { high: '128k', medium: '96k',  low: '64k' },
    libmp3lame: { high: '320k', medium: '192k', low: '128k' },
    libvorbis:  { high: '256k', medium: '160k', low: '96k' },
};

/* Which video codecs are valid in which container.
   First entry is the default for each container (when re-encoding). */
var CONTAINER_VIDEO_CODECS = {
    mp4:  ['libx264', 'libx265'],
    webm: ['libvpx'],                                  /* VP8 — VP9 is dead here */
    mkv:  ['libx264', 'libx265', 'libvpx'],
    mov:  ['libx264'],
    avi:  ['mpeg4', 'libx264'],                        /* legacy lane, see registry */
};

var CONTAINER_AUDIO_CODECS = {
    mp4:  ['aac', 'libmp3lame'],
    webm: ['libopus', 'libvorbis'],
    mkv:  ['aac', 'libopus', 'libvorbis', 'libmp3lame'],
    mov:  ['aac', 'libmp3lame'],
    avi:  ['libmp3lame', 'aac'],
};

/* ⭐ The REGISTRY (config/video-formats.php → window.VideoFormats) is the
   catalogue: what we offer and which encoder each container gets by default.
   The two tables above stay as the offline fallback — this module also runs on
   pages that render no format picker and therefore ship no snapshot — but when
   the registry IS present its codec wins as the default. That is what makes
   "add a container" a one-line config change instead of an edit here, in a
   Blade, and in every tool that copies the list. */
function containerCodecs(table, format, fallback) {
    var list = (table[format] || []).slice();
    var reg = window.VideoFormats && window.VideoFormats.get(format);
    var pref = reg && (table === CONTAINER_VIDEO_CODECS ? reg.video_codec : reg.audio_codec);
    if (pref) {
        list = [pref].concat(list.filter(function (c) { return c !== pref; }));
    }
    return list.length ? list : [fallback];
}

/* Map browser-reported MIME / extension → our format key. */
var EXT_TO_FORMAT = {
    'mp4': 'mp4', 'm4v': 'mp4', 'm4a': 'mp4',
    'webm': 'webm',
    'mkv': 'mkv',
    'mov': 'mov', 'qt': 'mov',
    'avi': 'mp4',  // AVI input → MP4 output (no AVI muxer in our FFmpeg build)
    'flv': 'mp4',
    '3gp': 'mp4',
};

var RESOLUTION_PRESETS = {
    '4k':    { w: 3840, h: 2160 },
    '1440p': { w: 2560, h: 1440 },
    '1080p': { w: 1920, h: 1080 },
    '720p':  { w: 1280, h: 720 },
    '480p':  { w: 854,  h: 480 },
    '360p':  { w: 640,  h: 360 },
};

/* FFmpeg's libass requires the ass filter, which only works in MP4 and MKV
   reliably (MOV occasionally OK). When the tool declares needsFilters with
   subtitle burn, restrict to these. */
var BURN_SAFE_FORMATS = ['mp4', 'mkv', 'mov'];

/* ═══════════════════════════════════════════
   Helpers
   ═══════════════════════════════════════════ */

function ext(filename){
    var i = (filename || '').lastIndexOf('.');
    return i >= 0 ? filename.substring(i + 1).toLowerCase() : '';
}

function baseName(filename){
    var i = (filename || 'video').lastIndexOf('.');
    return i > 0 ? filename.substring(0, i) : (filename || 'video');
}

/* Probe the input file via a hidden <video> element. Cheap (just metadata),
   gives us width / height / duration. Codec details require ffprobe (slow);
   we defer those to encode time when they actually matter. */
function probeMetadata(file){
    return new Promise(function(resolve){
        var url = URL.createObjectURL(file);
        var v = document.createElement('video');
        v.preload = 'metadata';
        v.muted = true;
        var done = false;
        var finish = function(meta){
            if (done) return; done = true;
            try { URL.revokeObjectURL(url); } catch(e){}
            resolve(meta);
        };
        v.onloadedmetadata = function(){
            finish({
                width:    v.videoWidth || 0,
                height:   v.videoHeight || 0,
                duration: v.duration || 0,
                hasAudio: !!(v.mozHasAudio || (v.webkitAudioDecodedByteCount > 0) || (v.audioTracks && v.audioTracks.length)),
            });
        };
        v.onerror = function(){ finish({ width:0, height:0, duration:0, hasAudio:true }); };
        v.src = url;
        /* Fail-safe: some MOVs / MKVs never fire loadedmetadata.
           Time out after 4s and fall back to defaults. */
        setTimeout(function(){ finish({ width:0, height:0, duration:0, hasAudio:true }); }, 4000);
    });
}

/* Pick first allowed codec for a container, given a preferred name.
   E.g. ('mp4', 'libx264') → 'libx264'; ('webm', 'libx264') → 'libvpx' (forced swap).
   A caller that explicitly asks for 'libvpx-vp9' also lands on 'libvpx': the
   codec is not in any container's allow-list any more, so resolved() swaps it. */
function pickCodec(allowed, preferred){
    if (preferred && allowed.indexOf(preferred) >= 0) return preferred;
    return allowed[0];
}

/* ═══════════════════════════════════════════
   VideoExporter class
   ═══════════════════════════════════════════ */

function VideoExporter(opts){
    opts = opts || {};
    this.inputFile = opts.inputFile;
    this.needsFilters    = !!opts.needsFilters;
    this.needsBurnIn     = !!opts.needsBurnIn;       // subtitle burn → restrict to BURN_SAFE_FORMATS
    this.allowedFormats  = opts.allowedFormats  || ['same', 'mp4', 'webm', 'mkv'];
    this.allowedQualities= opts.allowedQualities|| ['high', 'medium', 'low'];
    this.captionsAware   = !!opts.captionsAware;
    /* DEPRECATED — kept for back-compat; prefer per-call extra.videoCopy.
       audioOnly meant "video stream is `-c:v copy`, audio is the only thing
       being re-encoded". Confusing for tools like mute-video where the
       reverse is the common case (re-encode video, strip audio). */
    this.audioOnly       = !!opts.audioOnly;

    /* Detected from the input file. Filled by detect(). */
    this.detected = {
        format:     null,    // 'mp4' | 'webm' | ...
        ext:        null,    // raw extension
        width:      0,
        height:     0,
        duration:   0,
        hasAudio:   true,
    };

    /* User selection (or recommended defaults). All mutable via setters. */
    this.sel = {
        format:     'same',
        quality:    'medium',
        resolution: 'same',
        channels:   'preserve',  // 'preserve' | 'mono' | 'stereo'
        fps:        'same',      // 'same' | 24 | 30 | 60
        videoCodec: 'auto',      // 'auto' picks first compatible for chosen format
        audioCodec: 'auto',
    };

    this.uiRoot = null;
    this.changeListeners = [];
}

VideoExporter.prototype.on = function(event, fn){
    if (event === 'change') this.changeListeners.push(fn);
    return this;
};

VideoExporter.prototype._emitChange = function(){
    for (var i = 0; i < this.changeListeners.length; i++){
        try { this.changeListeners[i](this); } catch(e){ console.warn('[VideoExporter] listener error', e); }
    }
};

VideoExporter.prototype.detect = async function(){
    if (!this.inputFile) return this.detected;
    var fileExt = ext(this.inputFile.name);
    this.detected.ext    = fileExt;
    this.detected.format = EXT_TO_FORMAT[fileExt] || 'mp4';

    var meta = await probeMetadata(this.inputFile);
    this.detected.width    = meta.width;
    this.detected.height   = meta.height;
    this.detected.duration = meta.duration;
    this.detected.hasAudio = meta.hasAudio;

    /* Recommend defaults based on detection. "Same" stays for format. */
    this.sel.format = this._normaliseFormat('same');
    return this.detected;
};

/* Map "same" → resolved container, applying tool restrictions. */
VideoExporter.prototype._normaliseFormat = function(format){
    var resolved = format;
    if (resolved === 'same') resolved = this.detected.format || 'mp4';
    /* Burn-in restricts to libass-safe containers. */
    if (this.needsBurnIn && BURN_SAFE_FORMATS.indexOf(resolved) < 0) resolved = 'mp4';
    /* Tool-level restriction. */
    if (this.allowedFormats.indexOf(format) < 0 && this.allowedFormats.indexOf(resolved) < 0) {
        resolved = this.allowedFormats[0] === 'same' ? this.detected.format : this.allowedFormats[0];
    }
    return resolved;
};

VideoExporter.prototype.setFormat = function(format){
    this.sel.format = format;
    this._emitChange();
    return this;
};
VideoExporter.prototype.setQuality = function(quality){
    if (this.allowedQualities.indexOf(quality) >= 0) this.sel.quality = quality;
    this._emitChange();
    return this;
};
VideoExporter.prototype.setResolution = function(res){
    this.sel.resolution = res;
    this._emitChange();
    return this;
};
VideoExporter.prototype.setChannels = function(ch){
    this.sel.channels = ch;
    this._emitChange();
    return this;
};
VideoExporter.prototype.setFps = function(fps){
    this.sel.fps = fps;
    this._emitChange();
    return this;
};
VideoExporter.prototype.setVideoCodec = function(c){
    this.sel.videoCodec = c;
    this._emitChange();
    return this;
};
VideoExporter.prototype.setAudioCodec = function(c){
    this.sel.audioCodec = c;
    this._emitChange();
    return this;
};

/* Return JUST the video codec args (-c:v <codec> + per-codec quality preset
   or qualityOverride). Used by tools that compose their own ffmpeg arg list
   (reels-maker has bespoke filter graphs + pre-args + audio chain) and only
   want VideoExporter for the format-dependent codec selection. */
VideoExporter.prototype.videoCodecArgs = function(qualityOverride){
    var r = this.resolved();
    var args = ['-c:v', r.videoCodec];
    if (qualityOverride && Array.isArray(qualityOverride)) {
        args = args.concat(qualityOverride);
    } else {
        var qPreset = (VIDEO_QUALITY[r.videoCodec] || VIDEO_QUALITY.libx264)[r.quality];
        if (qPreset) args = args.concat(qPreset);
    }
    return _fixVp8Rate(args, r.videoCodec);
};

/* Callers hand the SAME qualityOverride array to every container, and several
   of them carry `-b:v 0` — which is correct for x264 (it keeps CRF mode) and
   ruinous for VP8, where it silently means "ffmpeg's 200k default" and -crf
   stops governing. Repair it here, where the resolved codec is actually known,
   rather than asking five call sites to branch on the container.
   Note the inverse is a real trap too: forcing a non-zero -b:v onto libx264
   flips it out of CRF into ABR. So the fix is codec-scoped, never blanket. */
function _fixVp8Rate(args, codec){
    if (codec !== 'libvpx') return args;
    var i = args.indexOf('-b:v');
    if (i === -1) return args.concat(['-b:v', VP8_RATE_CAP]);
    if (String(args[i + 1]) === '0') args[i + 1] = VP8_RATE_CAP;
    return args;
}

/* Resolve the final output spec — what we actually intend to produce. */
VideoExporter.prototype.resolved = function(){
    var format = this._normaliseFormat(this.sel.format);
    var vList = containerCodecs(CONTAINER_VIDEO_CODECS, format, 'libx264');
    var aList = containerCodecs(CONTAINER_AUDIO_CODECS, format, 'aac');
    var videoCodec = this.sel.videoCodec === 'auto'
        ? pickCodec(vList, 'libx264')
        : this.sel.videoCodec;
    /* If picked codec isn't compatible with the chosen container, fall back. */
    if (vList.indexOf(videoCodec) < 0) {
        videoCodec = vList[0];
    }
    var audioCodec = this.sel.audioCodec === 'auto'
        ? aList[0]
        : this.sel.audioCodec;
    if (aList.indexOf(audioCodec) < 0) {
        audioCodec = aList[0];
    }
    return {
        format:     format,
        videoCodec: videoCodec,
        audioCodec: audioCodec,
        quality:    this.sel.quality,
        resolution: this.sel.resolution,
        channels:   this.sel.channels,
        fps:        this.sel.fps,
    };
};

/* Compute target resolution {w, h} or null (preserve). */
VideoExporter.prototype._targetResolution = function(){
    if (this.sel.resolution === 'same' || this.sel.resolution === 'auto') return null;
    if (typeof this.sel.resolution === 'object') return this.sel.resolution;
    return RESOLUTION_PRESETS[this.sel.resolution] || null;
};

/* Build a plan — the FFmpeg invocation parameters.
   `extraFilters`: simple filter chain → -vf (e.g. 'ass=subs.ass:fontsdir=.').
   `extraFilterComplex`: full filter_complex graph → -filter_complex.
                         Mutually exclusive with extraFilters. The graph must
                         end with a labelled output stream (e.g. '[vout]') and
                         the tool also passes `extraOutputMap` to bind it.
   `extraOutputMap`: array of -map values (e.g. ['[vout]', '0:a?']) — used
                     only with extraFilterComplex.
   `extraOutputArgs`: extra args appended at end (e.g. ['-pix_fmt', 'yuv420p']).
   `extraInputArgs`: extra input args (e.g. ['-ss', '5'] for trim start).
   `outputBase`: base name without extension. Defaults to inputFile basename + '-edited'. */
VideoExporter.prototype.plan = function(extra){
    extra = extra || {};
    var r = this.resolved();
    var args = [];
    var warnings = [];

    if (extra.extraInputArgs) args = args.concat(extra.extraInputArgs);

    /* Filter graph: (resize, if any) → (user filter) — captions-aware order means
       caption burn happens AFTER resize, so glyphs render at output resolution. */
    var filters = [];
    var resTarget = this._targetResolution();
    if (resTarget) {
        /* Force-even dimensions for libx264 (yuv420p requires even w/h). */
        filters.push('scale=' + resTarget.w + ':' + resTarget.h + ':force_original_aspect_ratio=decrease,pad=' + resTarget.w + ':' + resTarget.h + ':(ow-iw)/2:(oh-ih)/2');
    }
    if (extra.extraFilters) {
        if (this.captionsAware) {
            /* Caption burn comes after resize so subs render at final output size. */
            filters.push(extra.extraFilters);
        } else {
            /* No caption ordering concern — append filter normally. */
            filters.push(extra.extraFilters);
        }
    }
    /* extraFilterComplex takes precedence — used by multi-input tools (emoji
       overlay, video-merge, video-overlay) where the graph must reference
       labelled streams. -vf and -filter_complex are mutually exclusive on the
       same output. */
    if (extra.extraFilterComplex) {
        var fc = extra.extraFilterComplex;
        if (resTarget && filters.length) {
            /* Prepend resize as a separate chain feeding into the user's complex.
               User must reference [vsized] as input to their graph. We don't
               auto-rewrite — keep the contract explicit so tools control mapping. */
            warnings.push({ kind: 'resize_filter_complex',
                detail: 'Tool passed extraFilterComplex with a resolution preset; resize chain not auto-merged. Reference [vsized] from your graph if you need it.' });
            args.push('-filter_complex', '[0:v]' + filters[0] + '[vsized];' + fc);
        } else {
            args.push('-filter_complex', fc);
        }
        if (extra.extraOutputMap && extra.extraOutputMap.length) {
            for (var mi = 0; mi < extra.extraOutputMap.length; mi++) {
                args.push('-map', extra.extraOutputMap[mi]);
            }
        }
    } else if (filters.length) {
        args.push('-vf', filters.join(','));
    }

    /* FPS override. */
    if (this.sel.fps !== 'same' && typeof this.sel.fps === 'number') {
        args.push('-r', String(this.sel.fps));
    }

    /* Video stream:
       - extra.stripVideo:        -vn (e.g. extract-audio tool)
       - extra.videoCopy / audioOnly (legacy):  -c:v copy (no re-encode)
       - extra.qualityOverride:   array replaces the codec preset entirely.
                                  Used by tools that ship a per-tool CRF/preset
                                  the user is already accustomed to (mute-video
                                  exposes CRF 17/18/23/28 directly, WebM gets a
                                  bespoke 25/25/33/40 remap). The codec is still
                                  picked from container compatibility — only the
                                  quality args are overridden.
       - default: re-encode at chosen codec + built-in quality preset */
    var videoIsCopy = extra.videoCopy || this.audioOnly;
    if (extra.stripVideo) {
        args.push('-vn');
    } else if (videoIsCopy) {
        args.push('-c:v', 'copy');
    } else {
        args.push('-c:v', r.videoCodec);
        if (extra.qualityOverride && Array.isArray(extra.qualityOverride)) {
            args = args.concat(extra.qualityOverride);
        } else {
            var qPreset = (VIDEO_QUALITY[r.videoCodec] || VIDEO_QUALITY.libx264)[r.quality];
            if (qPreset) args = args.concat(qPreset);
        }
    }

    /* Audio stream:
       - extra.stripAudio: -an (caller-explicit drop)
       - extra.audioCopy: -c:a copy (caller-authoritative; trust over probe heuristic
         which returns hasAudio=false on Chrome by default — mozHasAudio is FF-only,
         webkitAudioDecodedByteCount stays 0 until playback, audioTracks is flag-gated.
         Trusting the caller here is what stops every burned MP4 from auto-captions /
         caption-styler / mute-video shipping silent on Chrome.)
       - no detected audio AND no caller signal: -an
       - default: re-encode at chosen audio codec + bitrate + channels */
    if (extra.stripAudio) {
        args.push('-an');
    } else if (extra.audioCopy) {
        args.push('-c:a', 'copy');
    } else if (!this.detected.hasAudio) {
        args.push('-an');
    } else {
        // libopus is ABSENT from the shipped @ffmpeg/core 0.12.10 build — it
        // dies with "memory access out of bounds" on EVERY invocation and
        // WEDGES the whole engine until reinit (config/formats.php documents
        // the same for the audio lane; re-measured 2026-07-21). WebM's first
        // container codec here is libopus, so any webm+audio export through
        // this exec path was a guaranteed crash. Swap to ffmpeg's built-in
        // `opus` encoder (needs -strict -2; stereo-max, which -ac below
        // already respects — the WASM opus encoder folds >2ch anyway).
        if (r.audioCodec === 'libopus') {
            args.push('-c:a', 'opus', '-strict', '-2');
        } else {
            args.push('-c:a', r.audioCodec);
        }
        var aBitrate = (AUDIO_BITRATE[r.audioCodec] || AUDIO_BITRATE.aac)[r.quality];
        if (aBitrate) args.push('-b:a', aBitrate);
        if (r.channels === 'mono')   args.push('-ac', '1');
        if (r.channels === 'stereo') args.push('-ac', '2');
    }

    /* Pixel format — yuv420p is the lingua franca for compatibility. */
    if (!videoIsCopy && !extra.stripVideo && r.videoCodec === 'libx264') {
        args.push('-pix_fmt', 'yuv420p');
    }

    if (extra.extraOutputArgs) args = args.concat(extra.extraOutputArgs);

    /* Warnings — surface honest deltas to the UI banner.
       "We saved as MP4 because your input was MOV which the browser can't preserve." */
    if (this.sel.format === 'same' && this.detected.format !== r.format) {
        warnings.push({
            kind: 'format_changed',
            from: this.detected.format,
            to:   r.format,
        });
    }
    if (this.sel.videoCodec === 'auto' && this.detected.format && r.format !== this.detected.format) {
        /* Cross-container → forced re-encode of video stream. */
        warnings.push({ kind: 'transcoded' });
    }

    /* Output file name. */
    var outBase = extra.outputBase || baseName((this.inputFile && this.inputFile.name) || 'video') + '-edited';
    var outputName = outBase + '.' + r.format;

    return {
        file:       this.inputFile,
        args:       args,
        outputName: outputName,
        resolved:   r,
        warnings:   warnings,
    };
};

/* ═══════════════════════════════════════════
   UI binding
   ═══════════════════════════════════════════ */

VideoExporter.prototype.bindUI = function(rootIdOrEl){
    var root = (typeof rootIdOrEl === 'string') ? document.getElementById(rootIdOrEl) : rootIdOrEl;
    if (!root) { console.warn('[VideoExporter] bindUI: container not found', rootIdOrEl); return this; }
    this.uiRoot = root;
    var self = this;

    /* Format buttons. */
    var formatBtns = root.querySelectorAll('[data-vx-format]');
    formatBtns.forEach(function(btn){
        var f = btn.getAttribute('data-vx-format');
        if (self.allowedFormats.indexOf(f) < 0) { btn.style.display = 'none'; return; }
        btn.addEventListener('click', function(){
            self.setFormat(f);
            self._refreshUI();
        });
    });

    /* Quality buttons. */
    var qualBtns = root.querySelectorAll('[data-vx-quality]');
    qualBtns.forEach(function(btn){
        var q = btn.getAttribute('data-vx-quality');
        if (self.allowedQualities.indexOf(q) < 0) { btn.style.display = 'none'; return; }
        btn.addEventListener('click', function(){
            self.setQuality(q);
            self._refreshUI();
        });
    });

    /* Advanced selects (resolution, channels, fps, codecs). */
    var advBindings = [
        ['[data-vx-resolution]', 'setResolution'],
        ['[data-vx-channels]',   'setChannels'],
        ['[data-vx-fps]',        'setFps'],
        ['[data-vx-vcodec]',     'setVideoCodec'],
        ['[data-vx-acodec]',     'setAudioCodec'],
    ];
    advBindings.forEach(function(pair){
        var el = root.querySelector(pair[0]);
        if (el) el.addEventListener('change', function(){
            var v = el.value;
            if (pair[0] === '[data-vx-fps]' && v !== 'same') v = parseInt(v, 10) || 'same';
            self[pair[1]](v);
            self._refreshUI();
        });
    });

    /* Advanced toggle. */
    var advToggle = root.querySelector('[data-vx-advanced-toggle]');
    var advPanel  = root.querySelector('[data-vx-advanced-panel]');
    if (advToggle && advPanel) {
        advToggle.addEventListener('click', function(){
            var open = advPanel.hasAttribute('hidden');
            if (open) advPanel.removeAttribute('hidden'); else advPanel.setAttribute('hidden', '');
            advToggle.setAttribute('aria-expanded', open ? 'true' : 'false');
        });
    }

    self._refreshUI();
    return this;
};

VideoExporter.prototype._refreshUI = function(){
    if (!this.uiRoot) return;
    var root = this.uiRoot;
    var sel = this.sel;

    root.querySelectorAll('[data-vx-format]').forEach(function(btn){
        var on = btn.getAttribute('data-vx-format') === sel.format;
        btn.classList.toggle('active', on);
        if (btn.hasAttribute('role') && btn.getAttribute('role') === 'radio') btn.setAttribute('aria-checked', on ? 'true' : 'false');
    });
    root.querySelectorAll('[data-vx-quality]').forEach(function(btn){
        var on = btn.getAttribute('data-vx-quality') === sel.quality;
        btn.classList.toggle('active', on);
        if (btn.hasAttribute('role') && btn.getAttribute('role') === 'radio') btn.setAttribute('aria-checked', on ? 'true' : 'false');
    });

    var setSel = function(selector, value){
        var el = root.querySelector(selector);
        if (el) el.value = String(value);
    };
    setSel('[data-vx-resolution]', sel.resolution);
    setSel('[data-vx-channels]',   sel.channels);
    setSel('[data-vx-fps]',        sel.fps);
    setSel('[data-vx-vcodec]',     sel.videoCodec);
    setSel('[data-vx-acodec]',     sel.audioCodec);

    /* Hide audio codec / channels selectors when input has no audio. */
    var audioSection = root.querySelector('[data-vx-audio-section]');
    if (audioSection) audioSection.style.display = this.detected.hasAudio ? '' : 'none';

    /* Banner: show resolved-vs-requested mismatch. */
    var banner = root.querySelector('[data-vx-warning]');
    if (banner) {
        var r = this.resolved();
        if (sel.format === 'same' && this.detected.format && r.format !== this.detected.format) {
            banner.textContent = banner.getAttribute('data-vx-changed-tpl')
                .replace('{from}', (this.detected.format || '?').toUpperCase())
                .replace('{to}',   r.format.toUpperCase());
            banner.hidden = false;
        } else {
            banner.hidden = true;
        }
    }
};

/* Public — expose for tools. */
window.VideoExporter = VideoExporter;
window.VideoExporter.QUALITY_MAP   = VIDEO_QUALITY;
window.VideoExporter.AUDIO_BITRATE = AUDIO_BITRATE;
window.VideoExporter.RESOLUTION_PRESETS = RESOLUTION_PRESETS;

})();
